import apiClient from "./ApiClient";

export const saveEmployee = async (employee) => {
  try {
    const response = await apiClient.post(`/api/Employee/save`, employee);
    return {
      success: true,
      message: response.data.msg || "Employee saved successfully",
      id: response.data.id
    };
  } catch (error) {
    console.error("Error saving employee:", error);
    throw {
      success: false,
      message: error.response?.data?.message || "Failed to save employee",
      error
    };
  }
};

// Function to get all reference data
const getReferenceData = async () => {
  try {
    const [businesses, departments, designations, branches, genders, countries] = await Promise.all([
      apiClient.post(`/api/Business/get`, {}),
      apiClient.post(`/api/Department/get`, {}),
      apiClient.post(`/api/Designation/get`, {}),
      apiClient.post(`/api/Branch/get`, {}),
      apiClient.get(`/api/Gender/get`),
      apiClient.post(`/api/Country/get`, {})
    ]);

    return {
      businesses: businesses.data,
      departments: departments.data,
      designations: designations.data,
      branches: branches.data,
      genders: genders.data,
      countries: countries.data
    };
  } catch (error) {
    console.error("Error fetching reference data:", error);
    throw error;
  }
};

// Get employees with all reference data resolved
export const getEmployeesWithDetails = async (filter) => {
  try {
    const [employees, referenceData] = await Promise.all([
      getEmployees(filter),
      getReferenceData()
    ]);

    // Map the employees data with reference data
    const enrichedEmployees = employees.map(employee => {
      const enriched = {
        ...employee,
        business_name: referenceData.businesses.find(b => b.business_Id === employee.employee_Business_Id)?.business_NameEn || '',
        department_name: referenceData.departments.find(d => d.department_Id === employee.employee_Department_ID)?.department_NameEn || '',
        designation_name: referenceData.designations.find(d => d.designation_Id === employee.employee_Designation_ID)?.designation_NameEn || '',
        branch_name: referenceData.branches.find(b => b.branch_Id === employee.employee_Branch_Id)?.branch_NameEn || '',
        gender_name: referenceData.genders.find(g => g.gender_Id === employee.employee_Gender_Id)?.gender_NameEn || '',
        country_name: referenceData.countries.find(c => c.country_Id === employee.employee_Country_Id)?.country_NameEn || ''
      };
      
      if (!enriched.business_name || !enriched.department_name || !enriched.designation_name || 
          !enriched.branch_name || !enriched.gender_name || !enriched.country_name) {
        // Some fields could not be enriched - silently continue
      }
      
      return enriched;
    });

    return enrichedEmployees;
  } catch (error) {
    console.error("Error fetching employees with details:", error);
    throw error;
  }
};

export const getEmployees = async (filter = {}) => {
  try {
    const response = await apiClient.post(`/api/Employee/get`, filter);
    return response.data;
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw error;
  }
};

export const getEmployeeByCode = async (employeeCode) => {
  try {
    const response = await apiClient.get(`/api/Employee/byCode/${employeeCode}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching employee by code:", error);
    if (error.response?.status === 404) {
      return null; // Employee not found
    }
    throw error;
  }
};

// API to search employees with pagination and search text
export const searchEmployees = async (searchText, page = 1, pageSize = 20, businessId = null, branchId = null) => {
  try {
    const payload = {
      searchText: searchText,
      page: page,
      pageSize: pageSize,
      activeOnly: true
    };
    
    if (businessId !== null && businessId !== undefined) {
      payload.business_Id = businessId;
    }
    if (branchId !== null && branchId !== undefined) {
      payload.branch_Id = branchId;
    }
    
    console.log('🌐 API Call - searchEmployees:', {
      url: `/api/Employee/search`,
      payload: payload,
    });
    
    const response = await apiClient.post(`/api/Employee/search`, payload);
    
    console.log('🌐 API Response - searchEmployees:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ API Error - searchEmployees:", error);
    console.error("❌ Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    throw error;
  }
};

// Get expiring documents for notifications
// OPTIMIZED: Uses getEmployees instead of getEmployeesWithDetails to avoid loading unnecessary reference data
export const getExpiringDocumentsForNotification = async () => {
  try {
    // Get user's companyId and branchId from localStorage
    const userCompanyId = localStorage.getItem("userCompanyId");
    const userBranchId = localStorage.getItem("userBranchId");
    
    // Build filter object - only include if values exist
    const filter = {};
    if (userCompanyId && userCompanyId !== 'null' && userCompanyId !== 'undefined') {
      filter.employee_Business_Id = parseInt(userCompanyId);
    }
    if (userBranchId && userBranchId !== 'null' && userBranchId !== 'undefined') {
      filter.employee_Branch_Id = parseInt(userBranchId);
    }
    
    // OPTIMIZED: Use getEmployees instead of getEmployeesWithDetails
    // We don't need reference data (businesses, departments, etc.) for notifications
    const employees = await getEmployees(filter);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    const expiringDocuments = [];
    
    employees.forEach(employee => {
      // Check Passport
      if (employee.employee_Passport_ExpDate) {
        const expiryDate = new Date(employee.employee_Passport_ExpDate);
        // Check if document is expired or expiring within 30 days
        if (expiryDate <= thirtyDaysFromNow) {
          const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          expiringDocuments.push({
            employeeId: employee.employee_Id,
            employeeName: employee.employee_NameEn,
            documentType: "Passport",
            documentNumber: employee.employee_PassportNumber || "N/A",
            expiryDate: expiryDate,
            daysLeft: daysLeft,
            isCritical: daysLeft <= 10
          });
        }
      }
      
      // Check Visa
      if (employee.employee_Visa_ExpDate) {
        const expiryDate = new Date(employee.employee_Visa_ExpDate);
        if (expiryDate <= thirtyDaysFromNow) {
          const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          expiringDocuments.push({
            employeeId: employee.employee_Id,
            employeeName: employee.employee_NameEn,
            documentType: "Visa",
            documentNumber: employee.employee_VisaNumber || "N/A",
            expiryDate: expiryDate,
            daysLeft: daysLeft,
            isCritical: daysLeft <= 10
          });
        }
      }
      
      // Check National ID
      if (employee.employee_NatID_ExpDate) {
        const expiryDate = new Date(employee.employee_NatID_ExpDate);
        if (expiryDate <= thirtyDaysFromNow) {
          const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          expiringDocuments.push({
            employeeId: employee.employee_Id,
            employeeName: employee.employee_NameEn,
            documentType: "Nationality ID",
            documentNumber: employee.employee_NatID || "N/A",
            expiryDate: expiryDate,
            daysLeft: daysLeft,
            isCritical: daysLeft <= 10
          });
        }
      }
      
      // Check Labour Card
      if (employee.employee_LabourCardExpiry) {
        const expiryDate = new Date(employee.employee_LabourCardExpiry);
        if (expiryDate <= thirtyDaysFromNow) {
          const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          expiringDocuments.push({
            employeeId: employee.employee_Id,
            employeeName: employee.employee_NameEn,
            documentType: "Labour Card",
            documentNumber: employee.employee_LabourCardNumber || "N/A",
            expiryDate: expiryDate,
            daysLeft: daysLeft,
            isCritical: daysLeft <= 10
          });
        }
      }
    });
    
    // Sort by urgency (critical first, then by days left)
    expiringDocuments.sort((a, b) => {
      if (a.isCritical && !b.isCritical) return -1;
      if (!a.isCritical && b.isCritical) return 1;
      return a.daysLeft - b.daysLeft;
    });
    
    return expiringDocuments;
  } catch (error) {
    console.error("Error fetching expiring documents:", error);
    return [];
  }
};
